import React from 'react';
import './HotelDetailsModal.css';

/**
 * HotelDetailsModal - Shows detailed hotel information
 * Opens when user clicks "View Details" on a hotel card
 */
const HotelDetailsModal = ({ hotel, onClose, onAddToTrip }) => {
  if (!hotel) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAddToTrip = () => {
    onAddToTrip(hotel);
    onClose();
  };

  return (
    <div className="hotel-modal-backdrop" onClick={handleBackdropClick}>
      <div className="hotel-modal-content">
        {/* Close Button */}
        <button className="hotel-modal-close" onClick={onClose}>
          ×
        </button>

        {/* Header */}
        <div className="hotel-modal-header">
          <div className="hotel-modal-title-section">
            <h2 className="hotel-modal-name">{hotel.name}</h2>
            {hotel.stars && (
              <div className="hotel-modal-stars">
                {'⭐'.repeat(hotel.stars)}
              </div>
            )}
            {hotel.address && (
              <div className="hotel-modal-address">
                📍 {hotel.address}
              </div>
            )}
          </div>
          <div className="hotel-modal-price-section">
            <div className="hotel-price-label">from</div>
            <div className="hotel-price-large">${hotel.pricePerNight || hotel.totalPrice}</div>
            <div className="hotel-price-per">per night</div>
          </div>
        </div>

        {/* Body */}
        <div className="hotel-modal-body">
          {/* Room Type */}
          {hotel.roomType && (
            <div className="hotel-detail-section">
              <h3 className="hotel-detail-heading">Room Type</h3>
              <div className="hotel-room-badge">{hotel.roomType}</div>
            </div>
          )}

          {/* Amenities */}
          <div className="hotel-detail-section">
            <h3 className="hotel-detail-heading">Amenities</h3>
            <div className="hotel-amenities-grid">
              {hotel.breakfast && (
                <div className="hotel-amenity-item">
                  <span className="amenity-icon">🍳</span>
                  <span>Breakfast Included</span>
                </div>
              )}
              {hotel.wifi && (
                <div className="hotel-amenity-item">
                  <span className="amenity-icon">📶</span>
                  <span>Free WiFi</span>
                </div>
              )}
              {hotel.parking && (
                <div className="hotel-amenity-item">
                  <span className="amenity-icon">🅿️</span>
                  <span>Free Parking</span>
                </div>
              )}
              {hotel.gym && (
                <div className="hotel-amenity-item">
                  <span className="amenity-icon">💪</span>
                  <span>Fitness Center</span>
                </div>
              )}
              {hotel.pool && (
                <div className="hotel-amenity-item">
                  <span className="amenity-icon">🏊</span>
                  <span>Pool</span>
                </div>
              )}
              {hotel.airConditioning && (
                <div className="hotel-amenity-item">
                  <span className="amenity-icon">❄️</span>
                  <span>Air Conditioning</span>
                </div>
              )}
            </div>
          </div>

          {/* Cancellation Policy */}
          {hotel.freeCancellation !== undefined && (
            <div className="hotel-detail-section">
              <h3 className="hotel-detail-heading">Cancellation Policy</h3>
              <div className={`hotel-cancellation ${hotel.freeCancellation ? 'free' : 'non-refundable'}`}>
                <span className="cancellation-icon">
                  {hotel.freeCancellation ? '✓' : '⚠️'}
                </span>
                <span>
                  {hotel.freeCancellation 
                    ? 'Free cancellation up to 24 hours before check-in'
                    : 'Non-refundable - No cancellations allowed'}
                </span>
              </div>
            </div>
          )}

          {/* Stay Details */}
          <div className="hotel-detail-section">
            <h3 className="hotel-detail-heading">Your Stay</h3>
            <div className="hotel-stay-details">
              <div className="stay-detail-row">
                <span className="stay-label">Check-in:</span>
                <span className="stay-value">{hotel.checkIn}</span>
              </div>
              <div className="stay-detail-row">
                <span className="stay-label">Check-out:</span>
                <span className="stay-value">{hotel.checkOut}</span>
              </div>
              {hotel.nights && (
                <div className="stay-detail-row">
                  <span className="stay-label">Total nights:</span>
                  <span className="stay-value">{hotel.nights} nights</span>
                </div>
              )}
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="hotel-detail-section">
            <h3 className="hotel-detail-heading">Price Breakdown</h3>
            <div className="hotel-price-breakdown">
              <div className="price-breakdown-row">
                <span>Room rate ({hotel.nights || 1} {hotel.nights === 1 ? 'night' : 'nights'})</span>
                <span>${hotel.pricePerNight || hotel.totalPrice} × {hotel.nights || 1}</span>
              </div>
              {hotel.taxes && (
                <div className="price-breakdown-row">
                  <span>Taxes & fees</span>
                  <span>${hotel.taxes}</span>
                </div>
              )}
              <div className="price-breakdown-total">
                <span>Total</span>
                <span>${hotel.totalPrice}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="hotel-modal-footer">
          <button className="hotel-modal-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="hotel-modal-add" onClick={handleAddToTrip}>
            Add to Trip
          </button>
        </div>
      </div>
    </div>
  );
};

export default HotelDetailsModal;